import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  convertDateToDotDate,
  formatPhoneNumber,
  getAge,
} from '../../../../../utils/sharedFunctions'
import {
  calculatePatientIntegration,
  getDemographicDisplayByCode,
  getDemographicDisplayByCodeArray,
} from '../../../../../components/common/shared/sharedPatientLogic'
import { selectDemographics } from '../../../../../redux/demographics/demographicsSlice'
import PrimaryInfoEditCard from './PrimaryInfoEditCard'
import './PanelPatientCard.scss'
import { PanelPatientDataDisplay } from '../sharedPanelComponents/PanelPatientDataDisplay'
import { capitalize } from 'lodash'
import PatientDemoEditCard from './PatientDemoEditCard'
import FEATURES from '../../../../../utils/featuresConstants'
import PatientMiscEditCard from './PanelMiscEditCard'
import { getPCPObject } from '../../../../../components/common/shared/sharedPatientCode'

function getGender(g) {
  return g === 'F' ? 'Female' : g === 'M' ? 'Male' : 'Unknown'
}

export default function PanelPatientCard() {
  const features = useSelector((state) => state?.user?.features)
  const panelEncounter = useSelector((state) => state.sidePanel.panelEncounter)
  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const demographicCodes = useSelector(selectDemographics)
  const showDemographicsAndAmendments = features?.includes(FEATURES.ONCDemographics)

  const pat_address = panelEncounter?.patient_address

  const [editPatientInfo, setEditPatientInfo] = useState(false)
  const [editPatientDemo, setEditPatientDemo] = useState(false)
  const [editPatientPCP, setEditPatientPCP] = useState(false)
  // TODO build out, not MVP
  const [editPatientAmendments, setEditPatientAmendments] = useState(true)

  const [pccgTitle, setPccgTitle] = useState(null)

  useEffect(() => {
    getPCPObject(panelEncounter?.primary_care_physician?.primary_care_physician_id).then((res) => {
      setPccgTitle(res?.group && res.county ? `${res.group} - ${res.county}` : null)
    })
  }, [panelEncounter?.primary_care_physician?.primary_care_physician_id])

  const isPatientActive = panelEncounter?.patient_type === 'active'
  const { isConnected } = calculatePatientIntegration(panelEncounter)

  return (
    <div id={'PanelPatientCard'} className={`display-box ${editPatientInfo ? 'padShrink' : ''}`}>
      {editPatientInfo ? (
        <PrimaryInfoEditCard setEditPatientInfo={() => setEditPatientInfo(false)} />
      ) : (
        <>
          {isPatientActive && (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <i
                className="fa-regular fa-triangle-exclamation"
                style={{ color: 'red', fontSize: '1.3em', marginRight: '4px' }}
              ></i>
              {isConnected
                ? 'This patient is synced from an integrated facility and cannot be edited.'
                : 'To update patient demographics information, please contact your biller.'}
            </span>
          )}
          <div className={'patientDetails'}>
            <PanelPatientDataDisplay
              title={'Last Name'}
              value={capitalize(panelEncounter?.patient_lastname)}
            />
            <PanelPatientDataDisplay
              title={'First Name'}
              value={capitalize(panelEncounter?.patient_firstname)}
            />
            <PanelPatientDataDisplay
              title={'Middle Name'}
              value={capitalize(panelEncounter?.patient_middlename)}
            />
            <PanelPatientDataDisplay
              title={'Date of Birth'}
              value={
                <div>
                  {convertDateToDotDate(panelEncounter?.patient_dob)} (
                  {getAge(panelEncounter?.patient_dob)} yrs)
                </div>
              }
            />
            <PanelPatientDataDisplay title={'Gender'} value={getGender(panelEncounter?.gender)} />
            {(panelEncounter?.patient_cellphone || panelEncounter?.patient_homephone) && (
              <PanelPatientDataDisplay
                title={'Phone Number'}
                value={
                  formatPhoneNumber(panelEncounter?.patient_cellphone) ||
                  formatPhoneNumber(panelEncounter?.patient_homephone)
                }
              />
            )}
            {!isPatientActive && (
              <i className="fa-solid fa-pencil" onClick={() => setEditPatientInfo(true)}></i>
            )}
          </div>

          <div className={'patientDetails'}>
            <PanelPatientDataDisplay
              title={'Address'}
              value={capitalize(pat_address?.street_line_1)}
            />
            <PanelPatientDataDisplay
              title={'Address Line 2'}
              value={capitalize(pat_address?.street_line_2)}
            />
            <PanelPatientDataDisplay title={'City'} value={capitalize(pat_address?.city)} />
            <PanelPatientDataDisplay title={'State'} value={pat_address?.state} />
            <PanelPatientDataDisplay title={'Zip Code'} value={pat_address?.zip} />
          </div>
        </>
      )}

      <hr style={{ marginTop: 0 }} />
      {editPatientPCP ? (
        <PatientMiscEditCard setEditPatientPCP={setEditPatientPCP} />
      ) : (
        <div className={'patientDetails'}>
          <i
            className="fa-solid fa-pencil position-absolute end-0"
            onClick={() => setEditPatientPCP(true)}
            style={{ top: '-16px' }}
          ></i>
          <div className={'d-flex align-items-center gap-3'}>
            <PanelPatientDataDisplay
              title={'Hospice Enrolled'}
              value={
                panelEncounter?.patient_hospiceEnrolled != null &&
                (panelEncounter?.patient_hospiceEnrolled ? 'Yes' : 'No')
              }
            />
          </div>
          <div className={'d-flex align-items-center gap-3'}>
            <PanelPatientDataDisplay
              title={'ACO'}
              value={
                panelEncounter?.patient_acoEnrolled != null &&
                (panelEncounter?.patient_acoEnrolled ? 'Yes' : 'No')
              }
            />
          </div>
          {features?.includes(FEATURES.MedrinaInvoicePCPs) && (
            <div className={'d-flex align-items-center gap-3'}>
              <PanelPatientDataDisplay
                title={'Primary Care Physician'}
                value={
                  panelEncounter?.primary_care_physician
                    ? panelEncounter?.primary_care_physician.name +
                      '-' +
                      panelEncounter?.primary_care_physician.npi
                    : ''
                }
              />
              {panelEncounter?.primary_care_physician && (
                <PanelPatientDataDisplay
                  title={'Primary Care Physician Group'}
                  value={panelEncounter?.primary_care_physician ? pccgTitle : ' '}
                />
              )}
            </div>
          )}
        </div>
      )}
      <hr style={{ marginTop: 0 }} />
      {editPatientDemo ? (
        <PatientDemoEditCard setEditPatientDemo={setEditPatientDemo} />
      ) : (
        showDemographicsAndAmendments && (
          <div className={'patientDetails demoDisplay'}>
            <i className="fa-solid fa-pencil" onClick={() => setEditPatientDemo(true)}></i>
            <PanelPatientDataDisplay
              title={'Race'}
              value={getDemographicDisplayByCodeArray(
                demographicCodes.raceCodes,
                panelEncounter.race || [],
              )}
            />
            <PanelPatientDataDisplay
              title={'Ethnicity'}
              value={getDemographicDisplayByCode(
                demographicCodes.ethnicityCodes,
                panelEncounter.ethnicity,
              )}
            />
            <PanelPatientDataDisplay
              title={'Sexual Orientation'}
              value={getDemographicDisplayByCode(
                demographicCodes.sexualOrientationCodes,
                panelEncounter.sexualOrientation,
              )}
            />
            <PanelPatientDataDisplay
              title={'Gender Identity'}
              value={getDemographicDisplayByCode(
                demographicCodes.genderIdentityCodes,
                panelEncounter.genderIdentity,
              )}
            />
            <PanelPatientDataDisplay
              title={' Preferred Language'}
              value={getDemographicDisplayByCode(
                demographicCodes.languageCodes,
                panelEncounter.preferredLanguage,
              )}
            />
          </div>
        )
      )}
      {editPatientAmendments ? (
        <></>
      ) : (
        showDemographicsAndAmendments && (
          <div className={'d-flex flex-column'}>
            <div className={'d-flex justify-content-between align-items-center title'}>
              Patient Amendments
              {!isReadOnly && (
                <i className="fa-solid fa-plus" onClick={() => setEditPatientAmendments(true)}></i>
              )}
            </div>
            {panelEncounter?.amendments?.map((amend, index) => (
              <>
                <hr />
                <div key={'amend-' + index} className={'ps-3'}>
                  {amend.text}
                  {amend.createUserName && (
                    <div className={'d-flex justify-content-between amend-details'}>
                      Created by: {amend.createUserName}
                      <span>{convertDateToDotDate(amend.createDate)}</span>
                    </div>
                  )}
                  {amend.acknowledgeUserName && (
                    <div className={'d-flex justify-content-between amend-details'}>
                      Acknowledged by: {amend.acknowledgeUserName}
                      <span>{convertDateToDotDate(amend.acknowledgeDate)}</span>
                    </div>
                  )}
                </div>
              </>
            ))}
          </div>
        )
      )}
    </div>
  )
}
